/**
 * SUPABASE CLIENT - BULLETPROOF VERSION
 * Konfiguracja klienta Supabase dla frontendu
 * Zabezpieczenia: timeout, safeStorage, error handling
 * Konwencja: Sikorski (H = si naturalne, B = si bemol)
 */

import { createClient } from '@supabase/supabase-js'

// ============================================================================
// BUILD-TIME CHECK
// ============================================================================

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined

const isDev = import.meta.env.DEV
const isSupabaseConfigured = !!(supabaseUrl && supabaseAnonKey)

// W DEV rzucamy błąd - developer musi ustawić env
// W PROD logujemy błąd ale nie crashujemy - pokaże się w UI
if (!isSupabaseConfigured) {
  const errorMsg = 
    'Brak zmiennych środowiskowych Supabase. ' +
    'Upewnij się że VITE_SUPABASE_URL i VITE_SUPABASE_ANON_KEY są ustawione.'
  
  if (isDev) {
    throw new Error(errorMsg)
  } else {
    console.error('[Supabase Client] ' + errorMsg)
  }
}

export { isSupabaseConfigured }

// ============================================================================
// SAFE STORAGE - fallback dla incognito Safari i zablokowanego localStorage
// ============================================================================

const memoryStorage = new Map<string, string>()

const safeStorage = {
  getItem: (key: string): string | null => {
    try {
      const item = localStorage.getItem(key)
      if (item !== null) return item
    } catch {
      // localStorage nie działa (incognito, zablokowane)
    }
    return memoryStorage.get(key) ?? null
  },
  
  setItem: (key: string, value: string): void => {
    memoryStorage.set(key, value)
    try {
      localStorage.setItem(key, value)
    } catch {
      // localStorage nie działa - używamy tylko memory
    }
  },
  
  removeItem: (key: string): void => {
    memoryStorage.delete(key)
    try {
      localStorage.removeItem(key)
    } catch {
      // localStorage nie działa
    }
  },
}

// ============================================================================
// SUPABASE CLIENT
// ============================================================================

export const supabase = isSupabaseConfigured
  ? createClient(supabaseUrl!, supabaseAnonKey!, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: safeStorage,
        storageKey: 'sb-auth-token',
        flowType: 'pkce',
      },
    })
  : null as unknown as ReturnType<typeof createClient> // Typ dla TypeScript, runtime check w useAuth

// ============================================================================
// TIMEOUT WRAPPER dla getSession
// ============================================================================

interface SessionResponse {
  data: { session: { user: { id: string; email?: string }; access_token: string } | null }
  error: Error | null
}

export async function getSessionWithTimeout(timeoutMs = 5000): Promise<SessionResponse> {
  if (!supabase) {
    return { data: { session: null }, error: new Error('Supabase not configured') }
  }

  const timeoutPromise = new Promise<SessionResponse>((resolve) => {
    setTimeout(() => {
      resolve({ 
        data: { session: null }, 
        error: new Error(`getSession timeout after ${timeoutMs}ms`) 
      })
    }, timeoutMs)
  })

  const sessionPromise = supabase.auth.getSession()

  return Promise.race([sessionPromise, timeoutPromise])
}

// ============================================================================
// NUKE SESSION - atomowe wylogowanie i czyszczenie
// ============================================================================

export async function nukeSessionAndReload(): Promise<void> {
  if (!supabase) {
    window.location.reload()
    return
  }

  try {
    // Wyloguj lokalnie
    await supabase.auth.signOut({ scope: 'local' })
  } catch {
    // Ignorujemy błędy przy wylogowaniu
  }

  // Czyścimy wszystkie klucze sb-* z localStorage
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-')) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key))
  } catch {
    // localStorage nie działa
  }

  // Czyścimy memory storage
  memoryStorage.clear()

  // Hard reload
  window.location.reload()
}

// Typy dla tabel
export interface SessionRecord {
  id: string
  created_at: string
  user_id: string | null
  config: {
    minSemitones: number
    maxSemitones: number
    allowedQualities: string[]
    direction: string
    playbackMode: string
    minBaseMidi: number
    maxBaseMidi: number
    taskCount: number
  }
  total_tasks: number
  correct_answers: number
  incorrect_answers: number
  accuracy_percent: number
  duration_ms: number
  average_response_time_ms: number
}

export interface AnswerRecord {
  id: string
  created_at: string
  session_id: string
  task_id: string
  base_note: number
  semitones: number
  interval_size: number
  interval_quality: string
  interval_name_short: string
  direction: string
  selected_size: number | null
  selected_quality: string | null
  is_correct: boolean
  response_time_ms: number
}
